import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

export const SW_UPDATE_BUST_PARAM = 'sw_update_bust';
export const SW_UPDATE_VERSION_PARAM = 'sw_version';
export const SW_UPDATE_RELOAD_SESSION_KEY = 'lumix_sw_update_reload';

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Loading chunk',
  'ChunkLoadError',
  'Loading CSS chunk',
];

@Injectable({
  providedIn: 'root',
})
export class AppUpdateService {
  private readonly _chunkLoadError$ = new Subject<string>();
  public readonly chunkLoadError$ = this._chunkLoadError$.asObservable();

  constructor() {
    this.listenForChunkErrors();
  }

  private listenForChunkErrors(): void {
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      if (this.isChunkLoadError(event.reason)) {
        event.preventDefault();
        this._chunkLoadError$.next(String(event.reason?.message ?? event.reason));
      }
    });

    window.addEventListener('error', (event: ErrorEvent) => {
      if (this.isChunkLoadError(event.error)) {
        this._chunkLoadError$.next(String(event.error?.message ?? event.message));
      }
    });
  }

  public isChunkLoadError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error ?? '');
    return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
  }

  public async clearAllCaches(): Promise<void> {
    if (!('caches' in window)) {
      return;
    }

    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
  }

  public async unregisterServiceWorkers(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  }

  public async activatePendingServiceWorker(reloadId?: number): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration?.waiting) {
      return;
    }

    await new Promise<void>((resolve) => {
      const timeoutId = window.setTimeout(() => resolve(), 5000);

      navigator.serviceWorker.addEventListener(
        'controllerchange',
        () => {
          window.clearTimeout(timeoutId);
          resolve();
        },
        { once: true },
      );

      registration.waiting?.postMessage({
        type: 'SKIP_WAITING',
        body: { reloadId },
      });
    });
  }

  public async prepareForHardReload(): Promise<void> {
    await this.unregisterServiceWorkers();
    await this.clearAllCaches();
  }

  public hardReload(appVersion?: string): void {
    const url = new URL(window.location.origin);

    url.searchParams.set(SW_UPDATE_BUST_PARAM, Date.now().toString());

    if (appVersion) {
      url.searchParams.set(SW_UPDATE_VERSION_PARAM, appVersion);
    }

    sessionStorage.setItem(SW_UPDATE_RELOAD_SESSION_KEY, '1');
    window.location.replace(url.toString());
  }

  public isPostUpdateNavigation(url: URL = new URL(window.location.href)): boolean {
    return (
      url.searchParams.has(SW_UPDATE_BUST_PARAM) ||
      url.searchParams.has(SW_UPDATE_VERSION_PARAM) ||
      sessionStorage.getItem(SW_UPDATE_RELOAD_SESSION_KEY) === '1'
    );
  }

  public clearPostUpdateNavigationState(url: URL = new URL(window.location.href)): void {
    url.searchParams.delete(SW_UPDATE_BUST_PARAM);
    url.searchParams.delete(SW_UPDATE_VERSION_PARAM);
    sessionStorage.removeItem(SW_UPDATE_RELOAD_SESSION_KEY);

    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, '', nextUrl || '/');
  }

  public async recoverFromStaleDeployment(): Promise<void> {
    await this.unregisterServiceWorkers();
    await this.clearAllCaches();
    sessionStorage.setItem(SW_UPDATE_RELOAD_SESSION_KEY, '1');
    window.location.replace(`${window.location.origin}/?${SW_UPDATE_BUST_PARAM}=${Date.now()}`);
  }
}
